import type { PricingTier, PricingFaq } from '@/types/content';

export const pricingTiers: PricingTier[] = [
  {
    id: 'good',
    name: 'Good',
    description: 'Get organized. For small teams ready to professionalize.',
    price: 399,
    priceUnit: 'month',
    features: [
      '5 technicians included',
      'Timekeeping & GPS time clock',
      'Scheduling calendar',
      'Customer management',
      'Work orders & invoicing',
      'Mobile app (iOS + Android)',
      'QuickBooks sync',
      'Stripe payments',
      '250 SMS/month',
      'Email support',
    ],
    cta: {
      label: 'Book a Demo',
      href: '/demo?plan=good',
    },
  },
  {
    id: 'better',
    name: 'Better',
    description: 'Grow your business. For teams ready to scale operations.',
    price: 749,
    priceUnit: 'month',
    highlighted: true,
    features: [
      'Everything in Good, plus:',
      'Dispatch board & GPS tracking',
      'Clipboard (visual task boards)',
      'CRM / Sales pipeline',
      'Estimates & field bidding',
      'Fleet & asset management',
      'Inventory & supplier ordering',
      'Expense tracking (Expensify)',
      'Role-based dashboards',
      'Online booking widget',
    ],
    cta: {
      label: 'Book a Demo',
      href: '/demo?plan=better',
    },
  },
  {
    id: 'best',
    name: 'Best',
    description: 'Run like a pro. The complete business operating system.',
    price: 1099,
    priceUnit: 'month',
    features: [
      'Everything in Better, plus:',
      'EOS: V/TO & Accountability Chart',
      'L10 meetings (Rocks, Scorecard, IDS)',
      'AI Assistant (CurtBot)',
      'Conversation Intelligence',
      'Recruiting / ATS',
      'LMS (Courses & certifications)',
      'Pop-up coaching system',
      'Field training grading',
      'Collections dashboard',
    ],
    cta: {
      label: 'Book a Demo',
      href: '/demo?plan=best',
    },
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    description: 'Full platform, full support. For large operations with complex needs.',
    price: 'contact',
    priceUnit: 'month',
    features: [
      'Everything in Best, plus:',
      'Unlimited users',
      'Volume pricing on technicians',
      'All industry packs included',
      'Dedicated success manager',
      'White-glove implementation',
      'Custom integrations & API access',
      'Priority support (4-hour SLA)',
      'Quarterly business reviews',
      'Custom training',
    ],
    cta: {
      label: 'Contact Sales',
      href: '/demo?plan=enterprise',
    },
  },
];

// Additional technician pricing
export const additionalTechPrice = 59; // per month

export const pricingFaqs: PricingFaq[] = [
  {
    question: 'How many technicians are included?',
    answer:
      'All plans include 5 technicians. Additional technicians are $59/month each. Enterprise plans include unlimited users with volume pricing.',
  },
  {
    question: 'What makes OmniForge different from Jobber or ServiceTitan?',
    answer:
      "OmniForge isn't just field service software—it's a complete Business Operating System. You get FSM + CRM + EOS tools + LMS + ATS + Conversation Intelligence all in one platform. Companies typically pay $3,000-7,000/month buying these separately. We're 60-70% less.",
  },
  {
    question: 'What does implementation include?',
    answer:
      "Self-serve (free) includes video tutorials and email support. Guided Setup ($999) includes a 3-hour kickoff, data import, and 2 weeks of support. White-Glove ($2,999) includes full migration, 6 hours training, and 30 days priority support. Enterprise implementation is included.",
  },
  {
    question: 'Can I add industry-specific features?',
    answer:
      'Yes! Industry Packs add specialized tools: Window Cleaning Pro ($249/mo), High-Rise Pro ($399/mo), HVAC/Plumbing Pro ($449/mo), and General Trades Pro ($149/mo). Bundle pricing available.',
  },
  {
    question: 'Can I switch plans later?',
    answer:
      'Absolutely. You can upgrade or downgrade at any time. Changes take effect on your next billing cycle.',
  },
  {
    question: 'Do you offer discounts for annual billing?',
    answer:
      'Yes, annual billing saves you 20% compared to monthly billing. Contact us for custom enterprise pricing.',
  },
  {
    question: 'What payment methods do you accept?',
    answer:
      'We accept all major credit cards, ACH bank transfers, and can invoice for annual enterprise contracts.',
  },
  {
    question: 'Is my data secure?',
    answer:
      'Your data is encrypted at rest and in transit. We use enterprise-grade security with SOC 2 compliance and regular security audits.',
  },
  {
    question: 'Do you integrate with QuickBooks?',
    answer:
      'Yes! QuickBooks Online integration is included on all plans for seamless two-way accounting sync. We also integrate with Stripe, TSheets, Expensify, and more.',
  },
  {
    question: "This is cheaper than I expected. What's the catch?",
    answer:
      "We're new and hungry. We want market share. Early customers get the best deal and become our biggest advocates. The catch is you have to give us feedback so we keep building what you need.",
  },
];

// Feature comparison data for the pricing table
export interface ComparisonFeature {
  name: string;
  good: boolean | string;
  better: boolean | string;
  best: boolean | string;
  enterprise: boolean | string;
  category?: boolean; // If true, this row is a category header
}

export const comparisonFeatures: ComparisonFeature[] = [
  // Technicians & Pricing
  { name: 'Technicians & Pricing', good: '', better: '', best: '', enterprise: '', category: true },
  { name: 'Technicians Included', good: '5', better: '5', best: '5', enterprise: 'Unlimited' },
  { name: 'Additional Technicians', good: '$59/mo each', better: '$59/mo each', best: '$59/mo each', enterprise: 'Volume pricing' },

  // Core Operations
  { name: 'Core Operations', good: '', better: '', best: '', enterprise: '', category: true },
  { name: 'Timekeeping & GPS Time Clock', good: true, better: true, best: true, enterprise: true },
  { name: 'Scheduling Calendar', good: true, better: true, best: true, enterprise: true },
  { name: 'Work Orders', good: true, better: true, best: true, enterprise: true },
  { name: 'Invoicing', good: true, better: true, best: true, enterprise: true },
  { name: 'Customer Management', good: true, better: true, best: true, enterprise: true },
  { name: 'Mobile App (iOS + Android)', good: true, better: true, best: true, enterprise: true },
  { name: 'Basic Reporting', good: true, better: true, best: true, enterprise: true },

  // Operations & Growth
  { name: 'Operations & Growth', good: '', better: '', best: '', enterprise: '', category: true },
  { name: 'Dispatch Board', good: false, better: true, best: true, enterprise: true },
  { name: 'Clipboard (Visual Task Boards)', good: false, better: true, best: true, enterprise: true },
  { name: 'LSW (Leader Standard Work)', good: false, better: true, best: true, enterprise: true },
  { name: 'Estimates & Field Bidding', good: false, better: true, best: true, enterprise: true },
  { name: 'GPS Tracking', good: false, better: true, best: true, enterprise: true },
  { name: 'Online Booking Widget', good: false, better: true, best: true, enterprise: true },

  // Sales & CRM
  { name: 'Sales & CRM', good: '', better: '', best: '', enterprise: '', category: true },
  { name: 'Pipeline / CRM', good: false, better: true, best: true, enterprise: true },
  { name: 'Photo Bid → Auto Pipeline', good: false, better: true, best: true, enterprise: true },
  { name: 'AI Next Steps on Cards', good: false, better: false, best: true, enterprise: true },

  // Fleet & Assets
  { name: 'Fleet & Assets', good: '', better: '', best: '', enterprise: '', category: true },
  { name: 'Fleet Management', good: false, better: true, best: true, enterprise: true },
  { name: 'Vehicle Status Board', good: false, better: true, best: true, enterprise: true },
  { name: 'Maintenance Tracking', good: false, better: true, best: true, enterprise: true },
  { name: 'Asset & Equipment Tracking', good: false, better: true, best: true, enterprise: true },

  // Supply Chain & Finance
  { name: 'Supply Chain & Finance', good: '', better: '', best: '', enterprise: '', category: true },
  { name: 'Inventory Management', good: false, better: true, best: true, enterprise: true },
  { name: 'Supplier Ordering', good: false, better: true, best: true, enterprise: true },
  { name: 'Expense Tracking', good: false, better: true, best: true, enterprise: true },
  { name: 'Job Profitability', good: false, better: false, best: true, enterprise: true },
  { name: 'Collections Dashboard', good: false, better: false, best: true, enterprise: true },

  // Leadership & EOS
  { name: 'Leadership & EOS', good: '', better: '', best: '', enterprise: '', category: true },
  { name: 'V/TO (Vision Traction Organizer)', good: false, better: false, best: true, enterprise: true },
  { name: 'Accountability Chart', good: false, better: false, best: true, enterprise: true },
  { name: 'L10 Meetings', good: false, better: false, best: true, enterprise: true },
  { name: 'Rocks & Scorecard', good: false, better: false, best: true, enterprise: true },
  { name: 'Level Loaded Calendar', good: false, better: false, best: true, enterprise: true },

  // AI & Intelligence
  { name: 'AI & Intelligence', good: '', better: '', best: '', enterprise: '', category: true },
  { name: 'CurtBot AI Assistant', good: false, better: false, best: true, enterprise: true },
  { name: 'Conversation Intelligence', good: false, better: false, best: true, enterprise: true },
  { name: 'AI Meeting Notes', good: false, better: false, best: true, enterprise: true },
  { name: 'AI Vehicle Verification', good: false, better: false, best: true, enterprise: true },

  // People Development
  { name: 'People Development', good: '', better: '', best: '', enterprise: '', category: true },
  { name: 'Recruiting / ATS', good: false, better: false, best: true, enterprise: true },
  { name: 'LMS (Courses & Certifications)', good: false, better: false, best: true, enterprise: true },
  { name: 'Field Training Grading', good: false, better: false, best: true, enterprise: true },
  { name: 'Pop-Up Coaching', good: false, better: false, best: true, enterprise: true },
  { name: 'Badges & Achievements', good: false, better: false, best: true, enterprise: true },
  { name: 'Onboarding/Offboarding Workflows', good: false, better: false, best: true, enterprise: true },

  // Integrations
  { name: 'Integrations', good: '', better: '', best: '', enterprise: '', category: true },
  { name: 'QuickBooks Sync', good: true, better: true, best: true, enterprise: true },
  { name: 'Stripe Payments', good: true, better: true, best: true, enterprise: true },
  { name: 'TSheets Integration', good: true, better: true, best: true, enterprise: true },
  { name: 'Expensify Integration', good: false, better: true, best: true, enterprise: true },
  { name: 'Shopify Vendor Integration', good: false, better: true, best: true, enterprise: true },
  { name: 'Custom API Access', good: false, better: false, best: false, enterprise: true },

  // Support & Services
  { name: 'Support & Services', good: '', better: '', best: '', enterprise: '', category: true },
  { name: 'Email Support', good: true, better: true, best: true, enterprise: true },
  { name: 'Priority Support', good: false, better: true, best: true, enterprise: true },
  { name: 'Dedicated Success Manager', good: false, better: false, best: false, enterprise: true },
  { name: 'Implementation', good: 'Self-serve', better: 'Guided ($999)', best: 'White-glove ($2,999)', enterprise: 'Included' },
  { name: 'SLA Guarantee', good: false, better: false, best: false, enterprise: '4-hour' },
];

export function getTierById(id: string): PricingTier | undefined {
  return pricingTiers.find((t) => t.id === id);
}
